import { useNavigate, useSearchParams } from 'react-router-dom';
import { Monitor, Smartphone, Tablet, AlertTriangle, MousePointerClick, Video, Eye, CheckCircle, UserCircle2, Star } from 'lucide-react';
import { formatDateTime, formatDurationMs, truncateUrl, extractPath, getInitials } from '../../utils/formatters';
import { readSessionIdentity } from '../../utils/identity';
import type { Session } from '../../api/client';
import clsx from 'clsx';

interface SessionRowProps {
  session: Session;
  isWatched?: boolean;
}

export default function SessionRow({ session, isWatched }: SessionRowProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  function getDeviceIcon(device: string | undefined | null) {
    switch ((device || '').toLowerCase()) {
      case 'mobile':
        return <Smartphone className="h-4 w-4 text-gray-400" />;
      case 'tablet':
        return <Tablet className="h-4 w-4 text-gray-400" />;
      default:
        return <Monitor className="h-4 w-4 text-gray-400" />;
    }
  }

  // Normalize field names (API returns different names than Session type)
  const s = session as any;
  const device = s.device || s.deviceType || 'desktop';
  const durationMs = s.durationMs ?? (s.duration ? s.duration * 1000 : 0);
  const pageCount = s.pageCount ?? s.pagesVisited ?? 0;
  const entryRawUrl = s.entryPage || s.entryUrl || s.entry_url || '/';
  const entryPage = extractPath(entryRawUrl);
  const hasRecording = s.hasRecording ?? s.isRecording ?? false;
  const visitorId = s.visitorId || 'unknown';
  const browser = s.browser || '';
  const os = s.os || '';
  const country = s.country || '';
  const identity = readSessionIdentity(s.userId, s.userTraits);

  function handleView() {
    // Mark as watched
    const watched = JSON.parse(localStorage.getItem('vantage_watched_sessions') || '[]');
    if (!watched.includes(session.id)) {
      watched.push(session.id);
      // Keep last 500 entries
      if (watched.length > 500) watched.splice(0, watched.length - 500);
      localStorage.setItem('vantage_watched_sessions', JSON.stringify(watched));
    }
    // Navigate with page param so we can return to the same page
    const currentPage = searchParams.get('page') || '1';
    navigate(`/sessions/${session.id}?page=${currentPage}`);
  }

  return (
    <tr className={clsx(
      'transition-colors',
      isWatched
        ? 'bg-green-50/60 hover:bg-green-50'
        : 'hover:bg-gray-50',
    )}>
      {/* Visitor */}
      <td className="table-cell">
        <div className="flex items-center gap-2">
          <div className={clsx(
            'flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium',
            identity
              ? 'bg-brand-100 text-brand-700'
              : isWatched
                ? 'bg-green-100 text-green-600'
                : 'bg-gray-100 text-gray-600',
          )}>
            {identity ? (
              <UserCircle2 className="h-4 w-4" />
            ) : isWatched ? (
              <CheckCircle className="h-4 w-4" />
            ) : (
              getInitials(visitorId)
            )}
          </div>
          {identity ? (
            <div className="flex flex-col">
              <span className="flex items-center gap-1 text-xs font-medium text-gray-900">
                {identity.displayName || 'Identified user'}
                {identity.isSubscriber && (
                  <Star className="h-3 w-3 text-amber-500" aria-label="Subscriber" />
                )}
              </span>
              <span className="font-mono text-[11px] text-gray-500">
                {identity.emailMasked || identity.phoneMasked || (identity.customerId ? identity.customerId.slice(0, 8) : '')}
              </span>
            </div>
          ) : (
            <span className="font-mono text-xs text-gray-500">{visitorId.slice(0, 8)}</span>
          )}
        </div>
      </td>

      {/* Date */}
      <td className="table-cell text-gray-500">{formatDateTime(session.startedAt)}</td>

      {/* Duration */}
      <td className="table-cell">{formatDurationMs(durationMs)}</td>

      {/* Pages */}
      <td className="table-cell">{pageCount}</td>

      {/* Entry Page */}
      <td className="table-cell">
        <span className="text-xs text-gray-500" title={entryPage}>
          {truncateUrl(entryPage, 30)}
        </span>
      </td>

      {/* Device */}
      <td className="table-cell">
        <div className="flex items-center gap-1.5">
          {getDeviceIcon(device)}
          <div className="flex flex-col">
            <span className="text-xs capitalize">{device}</span>
            {(browser || os) && (
              <span className="text-xs text-gray-400">
                {[browser, os].filter(Boolean).join(' / ')}
              </span>
            )}
          </div>
        </div>
      </td>

      {/* Country */}
      <td className="table-cell text-gray-500">{country || '—'}</td>

      {/* Tags */}
      <td className="table-cell">
        <div className="flex items-center gap-1">
          {session.hasRageClicks && (
            <span className="badge-warning gap-1" title="Rage clicks detected">
              <MousePointerClick className="h-3 w-3" />
            </span>
          )}
          {session.hasErrors && (
            <span className="badge-danger gap-1" title="Errors occurred">
              <AlertTriangle className="h-3 w-3" />
            </span>
          )}
          {hasRecording && (
            <span className="badge-info gap-1" title="Recording available">
              <Video className="h-3 w-3" />
            </span>
          )}
        </div>
      </td>

      {/* Actions */}
      <td className="table-cell">
        <button
          onClick={handleView}
          className="inline-flex items-center gap-1 rounded-lg bg-brand-50 px-3 py-1.5 text-xs font-medium text-brand-700 transition-colors hover:bg-brand-100"
        >
          <Eye className="h-3 w-3" />
          View
        </button>
      </td>
    </tr>
  );
}
